import { slideDown, slideUp } from '@theme/utilities';

const SELECTORS = {
	accordion: '.accordion',
	tab: '.tab',
	tabMeta: '.tab__meta',
	tabContent: '.tab__content',
};

const CSS_CLASSES = {
	open: 'open',
	allowMultiple: 'accordion--allow-multiple',
};

const SLIDE_DURATION = 500;

class AccordionTab {
	readonly element: HTMLElement;
	readonly content: HTMLElement;
	readonly header: HTMLElement;

	constructor(tabElement: HTMLElement) {
		const content = tabElement.querySelector<HTMLElement>(SELECTORS.tabContent);
		const header = tabElement.querySelector<HTMLElement>(SELECTORS.tabMeta);

		if (!content || !header) {
			throw new Error('Accordion tab is missing its header or content element.');
		}

		this.element = tabElement;
		this.content = content;
		this.header = header;
	}

	get isOpen(): boolean {
		return this.element.classList.contains(CSS_CLASSES.open);
	}

	open(): void {
		if (this.isOpen) return;

		this.element.classList.add(CSS_CLASSES.open);
		this.content.removeAttribute('hidden');
		slideDown(this.content, SLIDE_DURATION, true);
	}

	async close(): Promise<void> {
		if (!this.isOpen) return;

		await slideUp(this.content, SLIDE_DURATION, true);
		this.element.classList.remove(CSS_CLASSES.open);
		this.content.setAttribute('hidden', 'until-found');
	}
}

class Accordion {
	readonly element: HTMLElement;
	readonly allowMultiple: boolean;
	readonly tabs: AccordionTab[];

	constructor(element: HTMLElement) {
		this.element = element;
		this.allowMultiple = element.classList.contains(CSS_CLASSES.allowMultiple);
		this.tabs = this.createTabs();

		this.init();
	}

	createTabs(): AccordionTab[] {
		return Array.from(this.element.querySelectorAll<HTMLElement>(SELECTORS.tab)).map((tabElement) => new AccordionTab(tabElement));
	}

	init(): void {
		this.openDefaultTab();
		this.bindEvents();
	}

	openDefaultTab(): void {
		const defaultIndex = parseInt(this.element.getAttribute('data-start-index') ?? '', 10);
		if (defaultIndex && this.tabs[defaultIndex - 1]) {
			this.tabs[defaultIndex - 1].open();
		}
	}

	bindEvents(): void {
		this.tabs.forEach((tab) => {
			// Handle browser's find-in-page functionality
			tab.element.addEventListener('beforematch', () => {
				if (!tab.isOpen) tab.open();
			});

			// Handle click events
			tab.header.addEventListener('click', () => {
				this.handleTabClick(tab);
			});
		});
	}

	handleTabClick(clickedTab: AccordionTab): void {
		if (clickedTab.isOpen) {
			clickedTab.close();
		} else {
			if (!this.allowMultiple) {
				this.closeAllTabs();
			}
			clickedTab.open();
		}
	}

	closeAllTabs(): void {
		this.tabs.filter((tab) => tab.isOpen).forEach((tab) => tab.close());
	}
}

// Initialize all accordions
document.querySelectorAll<HTMLElement>(SELECTORS.accordion).forEach((element) => new Accordion(element));
